import { BASE_SITE_TITLE, getFullPageTitle } from '~/constants/page-constants'
import type { Page, PageTitle, SourceId, CommonTrackingData } from '~/types/tracking'

type Params = {
  metaTitle: string
  metaDescription: string
  trackingPage: Page
  trackingPageTitle: PageTitle
  trackingSourceId?: SourceId
  shouldIndexPage?: boolean
  shouldTrackPage?: boolean
  metaImageUrl?: string
}

/**
 * This composable has to be used on EACH page in the app
 */
export const usePage = ({
  metaTitle,
  metaDescription,
  metaImageUrl,
  trackingPage,
  trackingPageTitle,
  trackingSourceId,
  shouldIndexPage = true,
  shouldTrackPage = true,
}: Params): void => {
  // in useTrackPage we use more informative metaTitle over trackingPage/trackingPageTitle
  const trackPage = useTrackPage(metaTitle)

  onMounted(() => {
    shouldTrackPage && trackPage()
  })

  const requestUrl = useRequestURL()
  const { origin } = requestUrl
  const fullRequestUrl = requestUrl.href

  const fullPageTitle = computed(() => getFullPageTitle(metaTitle))
  const metaImage = computed(() =>
    metaImageUrl
      ? { url: metaImageUrl }
      : {
          size: { width: 679, height: 365 },
          url: getIconSrc('og-image-679x365.png', { origin }),
        },
  )

  const commonTrackingData = computed<CommonTrackingData>(() => ({
    page: trackingPage,
    pageTitle: trackingPageTitle,
    sourceId: trackingSourceId,
  }))
  useSyncCommonTrackingDataState(commonTrackingData)

  const schemaScript = {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          'name': BASE_SITE_TITLE,
          'logo': getIconSrc('sendcloud-logo-tile-blue.svg', { origin }),
          'url': origin,
          'parentOrganization': {
            '@type': 'Organization',
            'name': 'Sendcloud',
            'url': 'https://www.sendcloud.com',
          },
        },
      },
    ],
  }

  useHead({
    title: fullPageTitle,
    link: [
      {
        rel: 'canonical',
        href: fullRequestUrl,
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: getIconSrc('favicon-32x32.png', { origin }),
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: getIconSrc('favicon-16x16.png', { origin }),
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: getIconSrc('apple-touch-icon-180x180.png', { origin }),
      },

      {
        rel: 'mask-icon',
        href: getIconSrc('safari-pinned-tab.svg', { origin }),
        color: '#5bbad5',
      },
      {
        rel: 'manifest',
        href: getIconManifestSrc(origin),
      },
    ],
    // useSeoMeta type does not know twitterUrl key exists
    meta: [{ name: 'twitter:url', content: fullRequestUrl }],
    ...schemaScript,
  })

  useSeoMeta({
    ogType: 'website',
    ogLocale: 'en_US',
    ogSiteName: BASE_SITE_TITLE,

    title: fullPageTitle,
    description: metaDescription,

    ogTitle: fullPageTitle,
    ogDescription: metaDescription,
    ogUrl: fullRequestUrl,
    ogImageAlt: fullPageTitle,
    ogImageUrl: metaImage.value.url,
    ...(metaImage.value.size && {
      ogImageWidth: metaImage.value.size.width,
      ogImageHeight: metaImage.value.size.height,
    }),

    twitterTitle: fullPageTitle,
    twitterDescription: metaDescription,
    twitterImageAlt: fullPageTitle,
    twitterImage: metaImage.value.url,
    twitterCard: 'summary_large_image',

    msapplicationTileColor: '#2b5797',
    themeColor: '#f6f6f9',
    robots: shouldIndexPage ? 'index, follow' : 'noindex, nofollow',
  })
}
